// assets/AllCourses/Exercises/FillInTheBlanksExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function FillInTheBlanksExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.fillInTheBlanksQuestions)
    ? exercise.fillInTheBlanksQuestions
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    questions: [],
    hasInteracted: false,
  });

  const completedRef = useRef(false);
  useEffect(() => {
  if (!showResults) completedRef.current = false;
}, [showResults]);

  /* ================= INIT / SYNC ATTEMPT ================= */
  useEffect(() => {
    if (!isPreview) return;

    const buildFresh = () => ({
      questions: questions.map(q => ({
        userAnswers: Array(
          (q.sentence.match(/_+/g) || []).length
        ).fill(""),
      })),
      hasInteracted: false,
    });

    if (!exercise.attempt) {
      setState(buildFresh());
      return;
    }

    // Backward compatibility
    if (Array.isArray(exercise.attempt.answers)) {
      setState({
        questions: questions.map((q, i) => ({
          userAnswers:
            exercise.attempt.answers[i] ||
            Array(
              (q.sentence.match(/_+/g) || []).length
            ).fill(""),
        })),
        hasInteracted: !!exercise.attempt.hasInteracted,
      });
    } else if (exercise.attempt.questions) {
      setState(exercise.attempt);
    } else {
      setState(buildFresh());
    }
  }, [isPreview, questions, exercise.attempt]);

  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise._id, isPreview]);

  /* ================= EDIT HELPERS ================= */
  const updateQuestions = next => {
    if (isEditMode)
      onUpdate("fillInTheBlanksQuestions", next);
  };

  const updateQuestionField = (qIdx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const current = next[qIdx] || {
      sentence: "",
      answers: [],
      requireCapitalization: false,
    };

    let updated = { ...current, [field]: value };

    if (field === "sentence") {
      const blanks =
        (value.match(/_+/g) || []).length;
      updated.answers = Array.from(
        { length: blanks },
        (_, i) => current.answers?.[i] || ""
      );
    }

    next[qIdx] = updated;
    updateQuestions(next);
  };

  /* ================= STUDENT INPUT ================= */
  const handleInputChange = (qIdx, blankIdx, value) => {
    if (isEditMode || showResults) return;

    setState(prev => {
      const next = structuredClone(prev);
      next.questions[qIdx].userAnswers[blankIdx] = value;
      next.hasInteracted = true;

      setAttempt(next);

      const allFilled = next.questions.every(q =>
        q.userAnswers.every(v => v.trim() !== "")
      );

      if (allFilled && !completedRef.current) {
        completedRef.current = true;
        checkAnswers(next);
      }

      return next;
    });
  };

  const checkAnswers = currentState => {
    const allCorrect = questions.every((q, qIdx) => {
      const blanks =
        (q.sentence.match(/_+/g) || []).length;

      return Array.from({ length: blanks }).every(
        (_, aIdx) => {
          const user = String(
            currentState.questions[qIdx]
              ?.userAnswers[aIdx] || ""
          ).trim();
          const correct = String(
            q.answers[aIdx] || ""
          ).trim();

          return q.requireCapitalization
            ? user === correct
            : user.toLowerCase() ===
                correct.toLowerCase();
        }
      );
    });

    onComplete(exercise._id, allCorrect);
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => {
          const blanks =
            (q.sentence.match(/_+/g) || []).length;

          return (
            <Card key={qIdx} className="p-6">
              <div className="space-y-6">
                <TextareaField
                  label={`Sentence ${qIdx + 1} (use _ for blanks)`}
                  value={q.sentence || ""}
                  onChange={v =>
                    updateQuestionField(
                      qIdx,
                      "sentence",
                      v
                    )
                  }
                  placeholder="The ___ is shining brightly."
                  rows={4}
                  required
                  error={
                    errors?.[
                      `exercise-${exercise.index}-sentence-${qIdx}`
                    ]
                  }
                />

                <div className="space-y-3">
                  <p className="font-medium">
                    Correct Answers
                  </p>

                  {blanks === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Add underscores (_) to create blanks
                    </p>
                  ) : (
                    Array.from({ length: blanks }).map(
                      (_, aIdx) => (
                        <StringField
                          key={aIdx}
                          label={`Blank ${aIdx + 1}`}
                          value={q.answers[aIdx] || ""}
                          onChange={v => {
                            const next = [...questions];
                            next[qIdx].answers[aIdx] = v;
                            updateQuestions(next);
                          }}
                          placeholder="Correct word"
                          required
                        />
                      )
                    )
                  )}
                </div>

                <CapitalizationRule
                  checked={!!q.requireCapitalization}
                  onChange={c =>
                    updateQuestionField(
                      qIdx,
                      "requireCapitalization",
                      c
                    )
                  }
                />

                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() =>
                    updateQuestions(
                      questions.filter(
                        (_, i) => i !== qIdx
                      )
                    )
                  }
                >
                  Remove Question
                </Button>
              </div>
            </Card>
          );
        })}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...questions,
              {
                sentence: "",
                answers: [],
                requireCapitalization: false,
              },
            ])
          }
        >
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>
            No fill-in-the-blanks questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle className="text-base">
          Fill in the Blanks
        </CardTitle>
        <CardDescription>
          Type the correct words to complete each
          sentence.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const parts = q.sentence
            .split(/(_+)/g)
            .filter(Boolean);

          const qState =
            state.questions[qIdx] || { userAnswers: [] };

          let blankIndex = 0;

          return (
            <div
              key={qIdx}
              className="rounded-xl border bg-muted/40 p-6 text-base leading-loose flex flex-wrap gap-3"
            >
              {parts.map((part, i) => {
                if (/_+/.test(part)) {
                  const bIdx = blankIndex++;
                  const userValue =
                    qState.userAnswers[bIdx] || "";
                  const correct = q.answers[bIdx] || "";

                 const isCorrect =
                      q.requireCapitalization
                        ? userValue.trim() === correct.trim()
                        : userValue.trim().toLowerCase() ===
                          correct.trim().toLowerCase();

                  return (
                    <div
                      key={i}
                      className="flex flex-col items-center"
                    >
                      <Input
                        value={userValue}
                        onChange={e =>
                          handleInputChange(
                            qIdx,
                            bIdx,
                            e.target.value
                          )
                        }
                        placeholder="____"
                        disabled={showResults}
                        className={cn(
                          "w-32 text-center font-medium transition border",
                          showResults && isCorrect &&
                            "bg-green-100 border-green-300",
                          showResults && !isCorrect && userValue &&
                            "bg-red-100 border-red-300",
                          showResults && "cursor-not-allowed opacity-95"
                        )}
                      />

                      {showResults && (
                       <span
                          className={cn(
                            "mt-1 text-xs font-medium",
                            isCorrect ? "text-green-600" : "text-red-600"
                          )}
                        >
                          {isCorrect
                            ? "Correct"
                            : `Correct: ${correct}`}
                        </span>
                      )}
                    </div>
                  );
                }

                return <span key={i}>{part}</span>;
              })}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
} 
